import { useState, useEffect, createContext, useContext } from 'react';
import {
  createResource,getResources,
  getResourceById
} from '../router/api';

/* ===================== TYPES ===================== */

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  est_superadmin?: boolean;
  actif?: boolean;
  role: {
    id: string | number;
    nom: string;
  };
  permissions: Record<
    string,
    {
      read: boolean;
      create: boolean;
      update: boolean;
      delete: boolean;
      validate: boolean;
    }
  >;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  updateProfile: (
    userData: Partial<User>
  ) => Promise<{ success: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
}

/* ===================== CONTEXT ===================== */

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/* ===================== PROVIDER ===================== */

export function useAuthProvider(): AuthContextType {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /* 🔄 Chargement initial */
  useEffect(() => {
    const bootstrap = async () => {
      const savedUser = localStorage.getItem('currentUser');
      const token = localStorage.getItem('authToken');

      if (savedUser && token) {
        setUser(JSON.parse(savedUser));
        await refreshUser();
      } else {
        signOut();
      }

      setLoading(false);
    };

    bootstrap();
  }, []);

  /* ===================== LOGIN ===================== */
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);

      const res = await createResource('auth/login', { email, password });
      const { user, token, message } = res.data;

      if (!user || !token) {
        return { success: false, error: message || 'Échec login' };
      }

      localStorage.setItem('authToken', token);
      await loadUserWithPermissions(user.id);

      return { success: true };
    } catch (err: any) {
      return {
        success: false,
        error: err.response?.data?.message || 'Serveur indisponible',
      };
    } finally {
      setLoading(false);
    }
  };

  /* ===================== LOGOUT ===================== */
  async function signOut() {
    try {
      await createResource('auth/logout', {});
    } catch {}

    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
  }

  /* ===================== UPDATE PROFIL ===================== */
  async function updateProfile(profile: Partial<User>) {
    if (!user) {
      return { success: false, error: 'Non connecté' };
    }

    const updated = { ...user, ...profile };
    setUser(updated);
    localStorage.setItem('currentUser', JSON.stringify(updated));

    return { success: true };
  }

  /* ===================== REFRESH USER ===================== */
  async function refreshUser() {
    if (!user) return;
    await loadUserWithPermissions(user.id);
  }

  /* ===================== LOAD USER + PERMISSIONS ===================== */
  async function loadUserWithPermissions(userId: string | number) {
    try {
      const res = await getResources(
        `auth/user/${userId}/permissions`
      );

      const data = res.data;

      const u: User = {
        id: String(data.user.id),
        name: data.user.name,
        email: data.user.email,
        avatar: data.user.avatar ?? undefined,
        est_superadmin: data.user.est_superadmin,
        actif: data.user.actif,
        role: {
          id: data.user.role.id,
          nom: data.user.role.nom,
        },
        permissions: data.permissions,
      };

      setUser(u);
      localStorage.setItem('currentUser', JSON.stringify(u));
    } catch (err) {
      console.error('❌ Session expirée');
      signOut();
    }
  }

  return {
    user,
    loading,
    signIn,
    signOut,
    updateProfile,
    refreshUser,
  };
}
